import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Audio,
  Img,
  staticFile,
} from "remotion";
import {TransitionSeries, linearTiming} from "@remotion/transitions";
import {SPRINGS, FONT_SCALE, SAFE_ZONES} from "../presets/editing-styles";
import {CLAMP, FONT_STACK, IDV} from "../presets/idv";
import {KineticText} from "../components/text/KineticText";
import {GlowText} from "../components/dimus/GlowText";
import {AccentLine} from "../components/dimus/AccentLine";
import {AudioTrack} from "../components/media/AudioTrack";
import {SceneBackground} from "../components/backgrounds/SceneBackground";
import {wipe} from "@remotion/transitions/wipe";
import {fade} from "@remotion/transitions/fade";
import {slide} from "@remotion/transitions/slide";
import {autoSfx} from "../utils/sfx-helper";
import {SERIES_BGM, scenesToDuckSegments} from "../presets/music";

/**
 * CAFÉ COM TEU ROI EP11 — RICH VERSION
 * "Cliente que reclama é cliente que ainda liga"
 *
 * CADA CENA TEM UM BACKGROUND DIFERENTE:
 *   Cena 1 (abertura):  floating-shapes — tech/moderno
 *   Cena 2 (princípio): grid-dots — editorial/dado
 *   Cena 3 (reflexão):  gradient-drift — cinematic
 *   Cena 4 (compromisso): dark-void — sério/minimal
 *   Cena 5 (CTA):       warm-glow — acolhedor/pessoal
 *
 * + KineticText (5 modos diferentes)
 * + BGM com ducking
 * + SFX automáticos
 * + Transições avançadas (lightLeak, glitch, zoomBlur)
 */

const YELLOW = IDV.third;
const GREEN = IDV.secondary;
const RED = IDV.error;
const BLUE = IDV.primary;
const WHITE = "#f5f5f7";
const MUTED = "#a3a3a3";
const SZ = SAFE_ZONES.universal;

const SCENES = [
  {dur: 125, file: "audio/cafe-ep11/cafe_ep11_c01.mp3"},
  {dur: 217, file: "audio/cafe-ep11/cafe_ep11_c02.mp3"},
  {dur: 750, file: "audio/cafe-ep11/cafe_ep11_c03.mp3"},
  {dur: 224, file: "audio/cafe-ep11/cafe_ep11_c04.mp3"},
  {dur: 223, file: "audio/cafe-ep11/cafe_ep11_c05.mp3"},
];

const TOTAL = SCENES.reduce((s, sc) => s + sc.dur, 0);
export const CAFE_EP11_RICH_TOTAL = TOTAL + 80; // 4 transitions × 20f

const bgm = SERIES_BGM["cafe-com-teu-roi"];
const duckSegments = scenesToDuckSegments(SCENES, 30);

// ─── Comparison Card ─────────────────────────────────────────────────
const CompareCard: React.FC<{
  title: string; subtitle: string; color: string; icon: string;
  delay: number; highlight?: boolean;
}> = ({title, subtitle, color, icon, delay, highlight}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.heavy});
  const glow = highlight && frame > delay + 20 ? 1 + Math.sin(frame * 0.06) * 0.03 : 1;
  if (frame < delay) return null;
  return (
    <div style={{
      opacity: p,
      transform: `translateY(${interpolate(p, [0, 1], [40, 0], CLAMP)}px) scale(${glow})`,
      padding: "28px 24px", borderRadius: 20, width: "85%",
      background: highlight ? `${color}12` : "rgba(255,255,255,0.03)",
      border: `2px solid ${highlight ? `${color}66` : "rgba(255,255,255,0.06)"}`,
      display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
      boxShadow: highlight ? `0 0 30px ${color}20` : "none",
      backdropFilter: "blur(4px)",
    }}>
      <div style={{fontSize: 48}}>{icon}</div>
      <div style={{fontSize: FONT_SCALE.h2.min, fontWeight: 800, color, fontFamily: FONT_STACK, textAlign: "center"}}>{title}</div>
      <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 500, color: MUTED, fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.3}}>{subtitle}</div>
    </div>
  );
};

// ─── Pill badge ──────────────────────────────────────────────────────
const Pill: React.FC<{text: string; color: string; delay: number; icon?: string}> = ({text, color, delay, icon}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.snappy});
  if (frame < delay) return null;
  return (
    <div style={{
      opacity: p, transform: `scale(${interpolate(p, [0, 1], [0.7, 1], CLAMP)})`,
      padding: "10px 20px", borderRadius: 999, display: "flex", alignItems: "center", gap: 8,
      background: `${color}18`, border: `1px solid ${color}44`,
      backdropFilter: "blur(8px)",
    }}>
      {icon && <span style={{fontSize: 20}}>{icon}</span>}
      <span style={{fontSize: FONT_SCALE.label.min, fontWeight: 700, color, fontFamily: FONT_STACK}}>{text}</span>
    </div>
  );
};

// ─── SCENE 1: ABERTURA — floating-shapes bg ──────────────────────────
const S1: React.FC = () => (
  <AbsoluteFill>
    <SceneBackground mode="floating-shapes" accent={GREEN} accent2={YELLOW} seed="ep11-s1" />
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 16}}>
      <Audio src={staticFile(SCENES[0].file)} volume={0.9} />
      <Pill text="☕ EP11" color={YELLOW} delay={5} />
      <KineticText text="CAFÉ COM TEU ROI" mode="bounce" delay={15} fontSize={FONT_SCALE.h1.min} fontWeight={900} color={WHITE} highlightWords={["ROI"]} highlightColor={YELLOW} />
      <AccentLine delay={40} color={YELLOW} width={200} />
    </AbsoluteFill>
  </AbsoluteFill>
);

// ─── SCENE 2: PRINCÍPIO — grid-dots bg ───────────────────────────────
const S2: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const contrastP = spring({frame: frame - 80, fps, config: SPRINGS.smooth});

  return (
    <AbsoluteFill>
      <SceneBackground mode="grid-dots" accent={GREEN} />
      <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 24}}>
        <Audio src={staticFile(SCENES[1].file)} volume={0.9} />
        <Pill text="PRINCÍPIO DO DIA" color={GREEN} delay={5} icon="💡" />
        <KineticText text="Cliente que reclama é cliente que ainda liga." mode="stagger-up" delay={15} fontSize={FONT_SCALE.h2.min} fontWeight={700} color={WHITE} highlightWords={["reclama", "liga"]} highlightColor={GREEN} />
        <div style={{opacity: contrastP, transform: `translateY(${interpolate(contrastP, [0, 1], [15, 0], CLAMP)}px)`}}>
          <KineticText text="O problema é o que vai embora calado." mode="stagger-blur" delay={90} fontSize={FONT_SCALE.body.min} fontWeight={500} color={RED} />
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ─── SCENE 3: REFLEXÃO — gradient-drift bg + comparação ──────────────
const S3: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const showCards = frame >= 300;
  const showFinal = frame >= 550;

  return (
    <AbsoluteFill>
      <SceneBackground mode="gradient-drift" accent={GREEN} accent2={BLUE} />
      <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 20}}>
        <Audio src={staticFile(SCENES[2].file)} volume={0.9} />

        {frame < 320 && (
          <>
            <KineticText text="Se o cliente tivesse desistido, ele nem ligava." mode="stagger-up" delay={10} fontSize={FONT_SCALE.body.min} fontWeight={500} color={MUTED} />
            <KineticText text="Ele simplesmente sumia." mode="stagger-scale" delay={80} fontSize={FONT_SCALE.h2.min} fontWeight={700} color={WHITE} />
          </>
        )}

        {showCards && frame < 570 && (
          <div style={{display: "flex", flexDirection: "column", gap: 16, alignItems: "center", width: "100%"}}>
            <CompareCard title="Reclama" subtitle="Ainda quer que funcione" color={GREEN} icon="📞" delay={310} highlight />
            <div style={{
              fontSize: FONT_SCALE.label.min, fontWeight: 600, color: MUTED, fontFamily: FONT_STACK,
              opacity: spring({frame: frame - 400, fps, config: SPRINGS.smooth}),
            }}>vs</div>
            <CompareCard title="Sai calado" subtitle="Já desistiu de você" color={RED} icon="🚪" delay={360} />
          </div>
        )}

        {showFinal && (
          <div style={{textAlign: "center"}}>
            <GlowText text="Me dá um motivo pra ficar." delay={555} size={FONT_SCALE.h2.max} color={GREEN} />
            <div style={{
              opacity: spring({frame: frame - 620, fps, config: SPRINGS.smooth}),
              marginTop: 12,
            }}>
              <KineticText text="Diagnóstico de graça." mode="wave" delay={625} fontSize={FONT_SCALE.body.min} fontWeight={500} color={MUTED} />
            </div>
          </div>
        )}
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ─── SCENE 4: COMPROMISSO — dark-void bg ─────────────────────────────
const S4: React.FC = () => (
  <AbsoluteFill>
    <SceneBackground mode="dark-void" accent={YELLOW} />
    <AbsoluteFill style={{justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.left + 20}px ${SZ.bottom}px`, flexDirection: "column", gap: 20}}>
      <Audio src={staticFile(SCENES[3].file)} volume={0.9} />
      <Pill text="COMPROMISSO" color={YELLOW} delay={5} icon="🤝" />
      <KineticText text="Olhar a última reclamação." mode="stagger-up" delay={15} fontSize={FONT_SCALE.h2.min} fontWeight={700} color={WHITE} textAlign="left" />
      <KineticText text="Em vez de resolver rápido, entender o que ela tá pedindo." mode="stagger-blur" delay={60} fontSize={FONT_SCALE.body.min} fontWeight={500} color={MUTED} textAlign="left" />
    </AbsoluteFill>
  </AbsoluteFill>
);

// ─── SCENE 5: CTA — warm-glow bg ────────────────────────────────────
const S5: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const profileP = spring({frame: frame - 80, fps, config: SPRINGS.snappy});
  const closeP = spring({frame: frame - 140, fps, config: SPRINGS.smooth});

  return (
    <AbsoluteFill>
      <SceneBackground mode="warm-glow" accent={GREEN} />
      <AbsoluteFill style={{justifyContent: "center", alignItems: "center", gap: 20, flexDirection: "column", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
        <Audio src={staticFile(SCENES[4].file)} volume={0.9} />
        <Pill text="DESAFIO" color={GREEN} delay={5} icon="🎯" />
        <KineticText text="Liga pro último cliente que reclamou." mode="split-reveal" delay={15} fontSize={FONT_SCALE.body.min} fontWeight={600} color={WHITE} />

        <div style={{
          opacity: profileP, display: "flex", alignItems: "center", gap: 16,
          transform: `translateY(${interpolate(profileP, [0, 1], [20, 0], CLAMP)}px)`,
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: "50%", overflow: "hidden",
            border: `3px solid ${GREEN}`, boxShadow: `0 0 20px ${GREEN}30`,
          }}>
            <Img src={staticFile("assets/profiles/guilhermeribeiro-ai.jpg")} style={{width: "100%", height: "100%", objectFit: "cover"}} />
          </div>
          <div>
            <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>@guilhermeribeiro.ai</div>
            <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 400, color: MUTED, fontFamily: FONT_STACK}}>Café com teu ROI</div>
          </div>
        </div>

        <div style={{opacity: closeP, fontSize: FONT_SCALE.body.min, fontWeight: 500, color: MUTED, fontFamily: FONT_STACK, fontStyle: "italic"}}>
          Até amanhã.
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ─── MAIN ─────────────────────────────────────────────────────────────
export const CafeEp11Rich: React.FC = () => {
  const sfxElements = autoSfx(SCENES);

  return (
    <AbsoluteFill style={{backgroundColor: "#000"}}>
      {/* BGM with auto-ducking */}
      <AudioTrack
        src={staticFile(bgm.file)}
        volume={bgm.volume}
        duckDuringSegments={duckSegments}
        duckVolume={bgm.duckVolume}
        fadeInDurationSeconds={bgm.fadeInSeconds}
        fadeOutDurationSeconds={bgm.fadeOutSeconds}
        loop
      />

      {/* SFX layer */}
      {sfxElements}

      {/* Scenes with DIFFERENT backgrounds + advanced transitions */}
      <TransitionSeries>
        <TransitionSeries.Sequence durationInFrames={SCENES[0].dur}>
          <S1 />
        </TransitionSeries.Sequence>

        <TransitionSeries.Transition
          presentation={fade()}
          timing={linearTiming({durationInFrames: 20})}
        />

        <TransitionSeries.Sequence durationInFrames={SCENES[1].dur}>
          <S2 />
        </TransitionSeries.Sequence>

        <TransitionSeries.Transition
          presentation={slide()}
          timing={linearTiming({durationInFrames: 20})}
        />

        <TransitionSeries.Sequence durationInFrames={SCENES[2].dur}>
          <S3 />
        </TransitionSeries.Sequence>

        <TransitionSeries.Transition
          presentation={wipe()}
          timing={linearTiming({durationInFrames: 20})}
        />

        <TransitionSeries.Sequence durationInFrames={SCENES[3].dur}>
          <S4 />
        </TransitionSeries.Sequence>

        <TransitionSeries.Transition
          presentation={fade()}
          timing={linearTiming({durationInFrames: 20})}
        />

        <TransitionSeries.Sequence durationInFrames={SCENES[4].dur}>
          <S5 />
        </TransitionSeries.Sequence>
      </TransitionSeries>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
